import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { API_ORIGIN } from '../api/client';
import { useCalendar, useConnectCalendar, useDisconnectCalendar, useMe } from '../api/queries';
import { Broken, Button, Card, SkeletonRows, useNotices } from '../ui';
import { since } from '../lib/time';

/**
 * The calendar, as a setting: its state, when it last answered, and the one
 * thing to do about it. "Reconnect the calendar" on Meetings lands here
 * (`#calendar`). Before this the only calendar control was on the start
 * page, so a disconnected calendar had nowhere to be reconnected
 * (Vladimir, 2026-09-18).
 */
export function CalendarCard(): ReactElement {
  const me = useMe();
  const calendar = useCalendar();
  const connect = useConnectCalendar();
  const disconnect = useDisconnectCalendar();
  const { show } = useNotices();
  const { hash } = useLocation();
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (hash === '#calendar') ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [hash]);

  const grantReady = me.data?.calendar.grantReady ?? false;
  const allow = `${API_ORIGIN}/v1/auth/google/start?calendar=1&returnTo=${encodeURIComponent('/settings#calendar')}`;
  const doConnect = () =>
    connect.mutate(undefined, {
      onSuccess: () =>
        show({
          tone: 'success',
          text: 'Connected — meetings appear within a minute, already marked with whether they will record.',
        }),
      onError: (err) =>
        show({ tone: 'failure', text: `The calendar could not be connected: ${err.message}` }),
    });

  return (
    <section id="calendar" ref={ref}>
      <Card title="Calendar" flush>
        {calendar.isPending ? (
          <SkeletonRows rows={1} dense />
        ) : calendar.isError ? (
          <Broken what="The calendar" retry={() => void calendar.refetch()} />
        ) : (
          <div className="setting">
            <span className="k">Google Calendar</span>
            <div className="v">
              {calendar.data.connected ? (
                <>
                  <span className="pill pill-green">
                    <span className="dot dot-green" />
                    connected
                  </span>
                  <span className="why">
                    {calendar.data.lastSyncedAt
                      ? `answered ${since(calendar.data.lastSyncedAt)}`
                      : 'waiting for its first answer — about a minute'}
                    {' · '}the notetaker follows what is on it
                  </span>
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() =>
                      disconnect.mutate(undefined, {
                        onSuccess: () =>
                          show({
                            tone: 'success',
                            text: 'Disconnected — nothing new will be recorded.',
                          }),
                        onError: (err) => show({ tone: 'failure', text: err.message }),
                      })
                    }
                    working={disconnect.isPending ? 'Disconnecting…' : false}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <>
                  <span className={`pill ${calendar.data.status ? 'pill-red' : ''}`}>
                    {calendar.data.status ? 'stopped answering' : 'not connected'}
                  </span>
                  <span className="why">
                    {calendar.data.status
                      ? 'Google withdrew our access, or the connection broke. Nothing has been recorded since. Reconnecting takes one click.'
                      : 'Meetings appear the moment it is connected, and the notetaker joins the ones with a link.'}
                  </span>
                  {grantReady ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={doConnect}
                      working={connect.isPending ? 'Connecting…' : false}
                    >
                      {calendar.data.status ? 'Reconnect' : 'Connect'}
                    </Button>
                  ) : (
                    <a className="btn btn-primary btn-sm" href={allow}>
                      Allow calendar access
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {calendar.data?.connected && !calendar.data.expandsGroups ? (
          <div className="setting">
            <span className="k">Invited groups</span>
            <div className="v">
              <span className="pill">counted as one person</span>
              <span className="why">
                A meeting invited as a group — <span className="mono">alpha@company.com</span> —
                lists one attendee instead of the people in it, so they are not given the recording
                and the brief cannot say who is coming. Allowing it asks Google for one more
                permission: to read the members of groups you are in.
              </span>
              <a className="btn btn-primary btn-sm" href={allow}>
                Read the people in a group
              </a>
            </div>
          </div>
        ) : null}
      </Card>
    </section>
  );
}
